import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import List from "@material-ui/core/List";
import { makeStyles } from "@material-ui/styles";
import Pagination from "@mui/material/Pagination";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import { getUserInfo } from "../reducks/users/selectors";
import { OrderHistoryItem } from "../components/products";
import { updateOrderStatusToFireStore } from "../functions/orders";
import { createOrderList } from "../functions/order/createOrderList";
import { ScrollButton } from "../components/UIkit";
import TextInputForFilter from "../components/UIkit/TextInputForFilter";
import OrderSearchPanel from "../components/order/search/OrderSearchPanel";
import { SEARCH_SCREENS } from "../functions/order/orderSearchModel";
import { useOrderSearchConditions } from "../hooks/useOrderSearchConditions";
import { useOrdersSubscription } from "../hooks/useOrdersSubscription";

const useStyles = makeStyles((theme) => ({
  orderList: {
    background: theme.palette.grey["100"],
    margin: "0 auto",
    padding: 32,
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
    [theme.breakpoints.up("md")]: {
      width: 768,
    },
  },
  menuContents: {
    margin: "0 auto",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
    [theme.breakpoints.up("md")]: {
      width: 768,
    },
  },
  // 発注番号・表示件数・ページ番号を1行に(狭い画面では縦積み)
  controls: {
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 8,
    [theme.breakpoints.down("sm")]: {
      display: "block",
    },
  },
  pageSizeSelect: {
    fontSize: 14,
    minWidth: 90,
  },
  pagination: {
    display: "flex",
    justifyContent: "center",
    marginTop: theme.spacing(2),
  },
}));

const OrderHistory = () => {
  const classes = useStyles();
  const userInfo = useSelector(getUserInfo);

  // 検索条件は URL が唯一の情報源(URL > localStorage > ロール既定値 で復元)
  const { conditions, patchConditions, resetConditions, isReady } =
    useOrderSearchConditions(SEARCH_SCREENS.HISTORY);
  // 条件に基づく範囲だけを購読する(旧実装の「全件購読→毎回再取得」を置き換え)
  const { orders, isLoading, error } = useOrdersSubscription({
    conditions,
    loginUserEmail: userInfo.email,
    enabled: isReady,
  });

  // 明細をカート(order_id)単位の見出しにまとめる。発注番号の絞り込みもここで行う
  const orderList = useMemo(
    () => createOrderList(orders, conditions.orderNumber),
    [orders, conditions.orderNumber]
  );

  // 発注番号はキー入力のたびに URL を書き換えると煩いので、
  // ローカル state に受けて少し待ってから検索条件(URL)へ反映する
  const [orderNumberInput, setOrderNumberInput] = useState(
    conditions.orderNumber
  );
  useEffect(() => {
    setOrderNumberInput(conditions.orderNumber);
  }, [conditions.orderNumber]);
  useEffect(() => {
    if (orderNumberInput === conditions.orderNumber) return undefined;
    const timer = setTimeout(() => {
      patchConditions({ orderNumber: orderNumberInput });
    }, 400);
    return () => clearTimeout(timer);
    // 入力値の変化だけで発火させる(conditions を含めると反映直後に再スケジュールされる)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderNumberInput]);

  // ページネーション(1始まり)。
  // 旧実装は Pagination と TablePagination を上下で計4個表示していたが、
  // 上=表示件数+ページ番号 / 下=ページ番号 の各1つに整理した
  const [currentPage, setCurrentPage] = useState(1);
  const [ordersPerPage, setOrdersPerPage] = useState(5);
  const pageCount = Math.max(1, Math.ceil(orderList.length / ordersPerPage));
  const indexOfLastOrder = currentPage * ordersPerPage;
  const currentOrders = orderList.slice(
    indexOfLastOrder - ordersPerPage,
    indexOfLastOrder
  );

  // 絞り込みで件数が減ってページが範囲外になったら末尾ページへ寄せる
  useEffect(() => {
    if (currentPage > pageCount) {
      setCurrentPage(pageCount);
    }
  }, [pageCount, currentPage]);

  const handleChangePage = (event, newPage) => {
    setCurrentPage(newPage);
    window.scrollTo(0, 0); // ページ変更時にスクロールをトップに戻す
  };

  const handleChangeRowsPerPage = (event) => {
    setOrdersPerPage(parseInt(event.target.value, 10));
    setCurrentPage(1); // 表示件数を変えたら最初のページに戻る
  };

  //ステータスの更新が実行されたとき
  const updateOrderStatus = async (status, order, dateInput) => {
    try {
      await updateOrderStatusToFireStore(status, order, userInfo, dateInput);
    } catch (e) {
      alert(
        "ステータスの更新に失敗しました。通信環境を確認して再度お試しください。"
      );
    }
  };

  return (
    <section className="c-section-wrapin">
      <div className={classes.menuContents}>
        <OrderSearchPanel
          screen={SEARCH_SCREENS.HISTORY}
          conditions={conditions}
          onChange={patchConditions}
          onReset={resetConditions}
          isLoading={isLoading}
          error={error}
          resultCount={orderList.length}
        />
        <div className={classes.controls}>
          <TextInputForFilter
            fullWidth={false}
            label={"発注番号"}
            multiline={false}
            required={false}
            width={80}
            minRows={1}
            value={orderNumberInput}
            type={"number"}
            disabled={false}
            onChange={(event) => setOrderNumberInput(event.target.value)}
          />
          <Select
            variant="standard"
            className={classes.pageSizeSelect}
            value={ordersPerPage}
            onChange={handleChangeRowsPerPage}
          >
            {[5, 10, 20, 50].map((size) => (
              <MenuItem key={size} value={size}>
                {size}件表示
              </MenuItem>
            ))}
          </Select>
          <Pagination
            className={classes.pagination}
            count={pageCount}
            page={currentPage}
            onChange={handleChangePage}
            color="primary"
          />
        </div>
      </div>
      <List className={classes.orderList}>
        {currentOrders.map((order) => (
          <OrderHistoryItem
            order={order}
            key={order.order_id}
            orderNumber={conditions.orderNumber}
            updateOrderStatus={updateOrderStatus}
          />
        ))}
      </List>
      <div className={classes.menuContents}>
        <Pagination
          className={classes.pagination}
          count={pageCount}
          page={currentPage}
          onChange={handleChangePage}
          color="primary"
        />
      </div>
      <ScrollButton />
    </section>
  );
};

export default OrderHistory;
